/**
 * 🚀 AUDIT FIX: Migrado para React Query (Issue #2)
 * 
 * ANTES: useEffect + fetch manual (race conditions, sem cache)
 * DEPOIS: React Query com cache de 10min e auto-redirect
 * 
 * NOTE: Este hook faz uma segunda query ao profiles com campos adicionais
 * (email, role) que o authStore não busca. Ideal futuro: consolidar no authStore.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthStore } from "@/stores/authStore";
import { CACHE_TIMES } from "@/lib/cacheConfig";
import { logger } from "@/lib/logger";

interface Profile {
  id: string;
  name: string;
  email: string;
  role: string;
  photo: string | null;
}

export const useDashboardAuth = () => {
  const navigate = useNavigate();
  const { session, user, isLoading: isAuthLoading } = useAuthStore();

  // React Query para buscar perfil (cache de 10min)
  const { data: profile, isLoading: isProfileLoading, isError: isProfileError } = useQuery({
    queryKey: ['dashboard-profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      // AUDIT FIX: Explicit column selection instead of select('*')
      const { data, error } = await supabase
        .from('profiles')
        .select('id, name, email, role, photo')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        logger.error('[useDashboardAuth] Profile fetch error:', { error: error.message });
        throw error;
      }
      
      if (!data) {
        logger.warn('[useDashboardAuth] Profile not found for user:', { userId: user.id });
      }
      
      return data as Profile | null;
    },
    enabled: !!user,
    ...CACHE_TIMES.PROFILE, // 10min cache
    retry: 2,
  });

  // Redirect se não autenticado (após loading)
  useEffect(() => {
    // 1. Se o AuthStore ainda está inicializando, NÃO FAÇA NADA
    if (isAuthLoading) return;

    // 2. Só redirecione se terminou de carregar e NÃO tem sessão
    if (!session) {
      navigate('/autenticacao', { replace: true });
    }
  }, [isAuthLoading, session, navigate]);

  // Loading combinado: só mostra loading se NÃO temos dados em cache
  const isLoading = isAuthLoading || (!!user && isProfileLoading && !profile);
  
  // Profile not found (query completed but no data)
  const profileNotFound = !isAuthLoading && !isProfileLoading && !!user && !profile && !isProfileError;

  return { 
    profile, 
    loading: isLoading, 
    profileNotFound,
    profileError: isProfileError,
  };
};
